import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { admin, api } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import FeederForm, { emptyFeeder, toBody } from '../../components/admin/FeederForm.jsx'
import { Clips, Restreams } from '../../components/admin/MediaSections.jsx'
import TelemetryChart from '../../components/admin/TelemetryChart.jsx'
import { btn, Field, inputClass, Notice, SecretsBox, Section, useAction } from '../../components/admin/ui.jsx'
import { pickI18n } from '../../i18n'
import { parseTs, pct, timeAgo } from '../../lib.js'
import { ErrorState, Loading } from '../states.jsx'

const RANGES = [
  ['h6', 6],
  ['h24', 24],
  ['d7', 168],
]

function Stat({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-2xl bg-paper px-4 py-3">
      <span className="text-xs font-bold text-subtle">{label}</span>
      <span className="text-lg font-extrabold">{value ?? '—'}</span>
    </div>
  )
}

function Telemetry({ slug }) {
  const { t } = useTranslation()
  const [hours, setHours] = useState(24)
  const { data } = useApi(() => api.telemetry(slug, hours), [slug, hours])
  const rows = data ?? []
  const series = (key) => rows.map((r) => ({ t: parseTs(r.ts), v: r[key] }))
  return (
    <Section
      title={t('admin.sections.telemetry')}
      actions={
        <div className="flex gap-1 rounded-full bg-paper p-1" role="group">
          {RANGES.map(([key, h]) => (
            <button
              key={key}
              type="button"
              aria-pressed={hours === h}
              onClick={() => setHours(h)}
              className={`cursor-pointer rounded-full px-3 py-1.5 text-sm font-extrabold ${
                hours === h ? 'bg-ink text-on-ink' : 'text-subtle hover:text-ink'
              }`}
            >
              {t(`admin.range.${key}`)}
            </button>
          ))}
        </div>
      }
    >
      <div className={`grid gap-4 lg:grid-cols-3 ${data ? '' : 'opacity-50'}`}>
        <TelemetryChart title={t('admin.chart.battery')} points={series('battery_percent')} unit="%" domain={[0, 100]} />
        <TelemetryChart title={t('admin.chart.food')} points={series('food_level_percent')} unit="%" domain={[0, 100]} />
        <TelemetryChart title={t('admin.chart.temperature')} points={series('temperature_c')} unit="°C" digits={1} />
      </div>
    </Section>
  )
}

function Controls({ feeder, onDone }) {
  const { t } = useTranslation()
  const { status, busy, run } = useAction()
  const [otaUrl, setOtaUrl] = useState('')
  const cmd = (action, params, confirmText) => () => {
    if (confirmText && !window.confirm(confirmText)) return
    run(() => admin.command(feeder.slug, action, params), t('admin.cmd.sent')).then(onDone)
  }
  const buttons = [
    ['feed', 'dispense', {}],
    ['openLid', 'open_lid'],
    ['closeLid', 'close_lid'],
    ['siren', 'siren', { seconds: 5 }],
    ['sirenOff', 'siren', { seconds: 0 }],
    ['fanOn', 'fan', { on: true }],
    ['fanOff', 'fan', { on: false }],
    ['cameraOn', 'camera', { on: true }],
    ['cameraOff', 'camera', { on: false }],
    ['refresh', 'request_telemetry'],
  ]
  return (
    <Section title={t('admin.sections.controls')}>
      <div className="flex flex-wrap gap-2">
        {buttons.map(([label, action, params]) => (
          <button key={label} type="button" disabled={busy} className={label === 'feed' ? btn.primary : btn.ghost} onClick={cmd(action, params)}>
            {t(`admin.cmd.${label}`)}
          </button>
        ))}
        {feeder.camera_url && (
          <button
            type="button"
            disabled={busy}
            className={btn.ghost}
            onClick={() => run(() => admin.snapshot(feeder.slug), t('admin.cmd.snapshotDone')).then(onDone)}
          >
            {t('admin.cmd.snapshot')}
          </button>
        )}
        <button type="button" disabled={busy} className={btn.danger} onClick={cmd('reboot', {}, t('admin.cmd.rebootConfirm'))}>
          {t('admin.cmd.reboot')}
        </button>
      </div>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          cmd('ota', { url: otaUrl }, t('admin.cmd.otaConfirm'))()
        }}
      >
        <Field label={t('admin.cmd.otaUrl')} className="min-w-64 flex-1">
          <input className={inputClass} type="url" required placeholder="https://…/firmware.bin" value={otaUrl} onChange={(e) => setOtaUrl(e.target.value)} />
        </Field>
        <button type="submit" disabled={busy} className={btn.dark}>
          {t('admin.cmd.ota')}
        </button>
      </form>
      <Notice status={status} />
    </Section>
  )
}

function Schedule({ slug }) {
  const { t } = useTranslation()
  const { data } = useApi(() => admin.schedule(slug), [slug])
  const [slots, setSlots] = useState(null)
  const { status, busy, run } = useAction()
  const rows = slots ?? (data ?? []).map((s) => ({ ...s, at: s.at.slice(0, 5) }))
  const update = (i, patch) => setSlots(rows.map((r, j) => (i === j ? { ...r, ...patch } : r)))
  const save = () =>
    run(
      () => admin.setSchedule(slug, rows.map((r) => ({ at: r.at, portion_grams: Number(r.portion_grams), enabled: r.enabled }))),
      t('admin.saved'),
    )
  return (
    <Section title={t('admin.sections.schedule')}>
      <p className="text-sm text-subtle">{t('admin.sched.hint')}</p>
      <div className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3">
            <Field label={t('admin.sched.time')}>
              <input type="time" className={`${inputClass} w-32`} value={r.at} onChange={(e) => update(i, { at: e.target.value })} />
            </Field>
            <Field label={t('admin.sched.grams')}>
              <input type="number" min="5" max="200" className={`${inputClass} w-24`} value={r.portion_grams} onChange={(e) => update(i, { portion_grams: e.target.value })} />
            </Field>
            <label className="flex h-11 items-center gap-2 text-sm font-extrabold">
              <input type="checkbox" className="size-5 accent-teal" checked={r.enabled} onChange={(e) => update(i, { enabled: e.target.checked })} />
              {t('admin.sched.enabled')}
            </label>
            <button type="button" className={btn.ghost} aria-label={t('admin.delete')} onClick={() => setSlots(rows.filter((_, j) => j !== i))}>
              ✕
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn.ghost} onClick={() => setSlots([...rows, { at: '12:00', portion_grams: 30, enabled: true }])}>
          + {t('admin.sched.addSlot')}
        </button>
        <button type="button" disabled={busy} className={btn.primary} onClick={save}>
          {t('admin.save')}
        </button>
        <Notice status={status} />
      </div>
    </Section>
  )
}

function Settings({ feeder, onSaved }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form, setForm] = useState(() => ({
    ...emptyFeeder,
    ...feeder,
    name: { ...emptyFeeder.name, ...feeder.name },
    description: { ...emptyFeeder.description, ...feeder.description },
    camera_url: feeder.camera_url ?? '',
  }))
  const { status, busy, run } = useAction()
  const submit = async (e) => {
    e.preventDefault()
    const res = await run(() => admin.update(feeder.slug, toBody(form, { create: false })), t('admin.saved'))
    if (res) onSaved()
  }
  const remove = async () => {
    if (!window.confirm(t('admin.confirmDelete'))) return
    const ok = await run(() => admin.remove(feeder.slug))
    if (ok !== undefined) navigate('/admin')
  }
  return (
    <Section title={t('admin.sections.settings')}>
      <form onSubmit={submit} className="flex flex-col gap-5">
        <FeederForm value={form} onChange={setForm} />
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy} className={btn.primary}>
            {t('admin.save')}
          </button>
          <button type="button" className={btn.danger} onClick={remove}>
            {t('admin.delete')}
          </button>
          <Notice status={status} />
        </div>
      </form>
    </Section>
  )
}

function Hardware({ feeder, onDone }) {
  const { t } = useTranslation()
  const { status, busy, run } = useAction()
  const [prov, setProv] = useState(null)
  const rotate = async () => {
    if (!window.confirm(t('admin.prov.rotateConfirm'))) return
    const res = await run(() => admin.rotate(feeder.slug))
    if (res) {
      setProv(res)
      onDone()
    }
  }
  const tel = feeder.last_telemetry ?? {}
  return (
    <Section title={t('admin.sections.hardware')}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Device ID" value={feeder.device_id} />
        <Stat label={t('admin.mqtt')} value={feeder.mqtt_provisioned ? t('admin.mqttYes') : t('admin.mqttNo')} />
        <Stat label="Firmware" value={tel.fw} />
        <Stat label="Stream path" value={feeder.stream_path} />
      </div>
      <button type="button" disabled={busy} className={`${btn.ghost} self-start`} onClick={rotate}>
        {t('admin.prov.rotate')}
      </button>
      <Notice status={status} />
      {prov && <SecretsBox provisioning={prov} />}
    </Section>
  )
}

function Logs({ slug, version }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const commands = useApi(() => admin.commands(slug), [slug, version])
  const events = useApi(() => api.events(slug, 30), [slug, version])
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section title={t('admin.sections.commands')}>
        <ul className="flex flex-col divide-y divide-line text-sm">
          {(commands.data ?? []).slice(0, 20).map((c) => (
            <li key={c.id} className="flex flex-wrap justify-between gap-2 py-2">
              <span className="font-extrabold">
                {c.action}
                {Object.keys(c.params).length > 0 && <span className="font-semibold text-subtle"> {JSON.stringify(c.params)}</span>}
              </span>
              <span className={c.status === 'ok' ? 'font-bold text-teal' : c.status === 'error' ? 'font-bold text-live' : 'text-subtle'}>
                {c.status}
                {c.detail && ` · ${c.detail}`} · {timeAgo(c.created_at, lang)}
              </span>
            </li>
          ))}
        </ul>
      </Section>
      <Section title={t('admin.sections.events')}>
        <ul className="flex flex-col divide-y divide-line text-sm">
          {(events.data ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap justify-between gap-2 py-2">
              <span className="font-extrabold">{t(`events.${e.type}`, e.type)}</span>
              <span className="text-subtle">{timeAgo(e.ts, lang)}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  )
}

export default function AdminFeederPage() {
  const { slug } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const feeder = useApi(() => admin.feeder(slug), [slug])
  const [version, setVersion] = useState(0)
  const refresh = () => {
    setVersion((v) => v + 1)
    feeder.reload()
  }

  // Live-ish admin view: refresh status every 15 s.
  useEffect(() => {
    const id = setInterval(feeder.reload, 15000)
    return () => clearInterval(id)
  }, [feeder.reload])

  if (feeder.loading && !feeder.data) return <Loading />
  if (feeder.error) return <ErrorState notFound={feeder.error.status === 404} />
  const f = feeder.data
  const tel = f.last_telemetry ?? {}

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-8 pb-16 sm:px-8">
      <Link to="/admin" className="text-sm font-bold text-subtle no-underline hover:text-ink">
        {t('admin.back')}
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-[clamp(28px,3.5vw,40px)] font-black tracking-[-1px]">{pickI18n(f.name, lang)}</h1>
          <span className="flex items-center gap-2 text-sm font-bold text-muted">
            <span className={`size-2 rounded-full ${f.is_online ? 'bg-teal-soft' : 'bg-faint'}`} />
            {f.is_online ? t('admin.online') : t('admin.offline')} · {t('admin.lastSeen')}{' '}
            {f.last_seen_at ? timeAgo(f.last_seen_at, lang) : t('admin.never')} · {t(`admin.status.${f.status}`)}
          </span>
        </div>
        <Link to={`/feeders/${f.slug}`} className={`${btn.ghost} no-underline`}>
          {t('nav.watchLive')} →
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label={t('admin.battery')} value={pct(tel.battery_percent)} />
        <Stat label={t('admin.food')} value={pct(tel.food_level_percent)} />
        <Stat label={t('admin.temperature')} value={tel.temperature_c == null ? null : `${tel.temperature_c.toFixed(1)}°C`} />
        <Stat label={t('admin.solar')} value={tel.solar_voltage == null ? null : `${tel.solar_voltage.toFixed(1)} V`} />
        <Stat label={t('admin.signal')} value={tel.rssi == null ? null : `${tel.rssi} dBm`} />
        <Stat label={t('admin.viewers')} value={f.viewers} />
        <Stat label={t('admin.camera')} value={f.camera_url ? '✓' : t('admin.cameraNone')} />
      </div>

      <Telemetry slug={slug} />
      <Controls feeder={f} onDone={refresh} />
      <Schedule slug={slug} />
      <Clips slug={slug} />
      <Restreams slug={slug} />
      <Logs slug={slug} version={version} />
      <Hardware feeder={f} onDone={refresh} />
      <Settings key={f.updated_at ?? f.id} feeder={f} onSaved={refresh} />
    </main>
  )
}
